import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, AlertTriangle, ExternalLink, PackageCheck, CheckCircle, LifeBuoy } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { MarkDeliveredModal } from '@components/MarkDeliveredModal';
import { CorreiosTicketModal } from '@components/CorreiosTicketModal';
import { usePermissions } from '@hooks/usePermissions';
import { isTrackingLink } from '../../utils/tracking';
import {
  shippingReportService,
  PendingDeliveryItem,
} from '../../services/shippingReportService';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

// Formats an ISO datetime/date string to dd/mm/aaaa (Brazilian).
const formatDateTimeBR = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

// Formats a YYYY-MM-DD deadline (no timezone) to dd/mm/aaaa without any shift.
const formatDeadline = (ymd?: string | null) => {
  if (!ymd) return '—';
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return formatDateTimeBR(ymd);
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/**
 * Um pedido só permite abrir chamado nos Correios quando saiu pela integração direta — o sinal
 * é ter objeto de rastreio em [Shipping].[CorreiosPrepostagem]. A modalidade não serve como
 * critério: "MelhorEnvio - Correios" é outra transportadora, e a escolha do checkout pode dizer
 * "Correios" sem que exista pré-postagem.
 */
const correiosObjectsOf = (item: PendingDeliveryItem): string[] => item.correios_objects ?? [];

export const PendingDeliveryReportView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canMarkDelivered =
    checkPermission('orders.mark_delivered').hasPermission || checkPermission('*').hasPermission;

  const [items, setItems] = useState<PendingDeliveryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onlyLate, setOnlyLate] = useState(false);
  const [deliverTarget, setDeliverTarget] = useState<PendingDeliveryItem | null>(null);
  const [ticketTarget, setTicketTarget] = useState<PendingDeliveryItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await shippingReportService.getPendingDeliveryReport();
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const lateCount = items.filter((i) => i.is_late).length;
  const visible = onlyLate ? items.filter((i) => i.is_late) : items;
  // A coluna de ações também existe quando o usuário não pode dar baixa, desde que haja
  // algum pedido dos Correios para abrir chamado.
  const hasCorreiosItem = visible.some((i) => correiosObjectsOf(i).length > 0);
  const showActions = canMarkDelivered || hasCorreiosItem;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pedidos Pendentes de Entrega</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pedidos enviados (status "Enviado") aguardando confirmação de entrega, com data limite calculada em dias úteis.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" icon={<RefreshCw size={16} />} onClick={load} loading={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      {/* Summary / filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <PackageCheck size={16} className="text-emerald-600" />
          <span><strong>{items.length}</strong> pedidos pendentes</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <AlertTriangle size={16} />
          <span><strong>{lateCount}</strong> atrasado(s)</span>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer ml-auto">
          <input
            type="checkbox"
            checked={onlyLate}
            onChange={(e) => setOnlyLate(e.target.checked)}
            className="rounded border-slate-300"
          />
          Mostrar somente atrasados
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <Card padding="none">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-400">
              <Loader2 className="animate-spin mr-2" size={18} /> Carregando…
            </div>
          ) : visible.length === 0 ? (
            <div className="p-10 text-center text-slate-400">Nenhum pedido pendente de entrega.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Pedido</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Nº E-commerce</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Cliente</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Cidade/UF</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Modalidade</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Data Envio</th>
                    <th className="text-center px-3 py-2 font-medium text-slate-600">Prazo (dias úteis)</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Data Limite</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Status Envio</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600">Total</th>
                    <th className="text-center px-3 py-2 font-medium text-slate-600">Rastreio</th>
                    {showActions && (
                      <th className="text-center px-3 py-2 font-medium text-slate-600">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((it) => (
                    <tr
                      key={it.order_id}
                      className={`border-b border-slate-100 ${it.is_late ? 'bg-amber-50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {it.order_id}
                        {it.is_priority && (
                          <span className="ml-1 text-[10px] font-semibold text-rose-600">★</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{it.order_num_ecommerce || '—'}</td>
                      <td className="px-3 py-2 text-slate-700">{it.customer_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {it.ship_addr_city ? `${it.ship_addr_city}${it.ship_addr_state ? '/' + it.ship_addr_state : ''}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{it.shipping_modality || it.shipping_chosen_by_customer || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDateTimeBR(it.shipping_timestamp)}</td>
                      <td className="px-3 py-2 text-center text-slate-600">{it.delivery_days ?? '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className={it.is_late ? 'text-amber-800 font-medium' : 'text-slate-700'}>
                            {formatDeadline(it.delivery_deadline)}
                          </span>
                          {it.is_late && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                              <AlertTriangle size={11} /> Atrasado
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{it.shipping_status || '—'}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{formatCurrency(it.order_sell_total)}</td>
                      <td className="px-3 py-2 text-center">
                        {isTrackingLink(it.shipping_track_url) ? (
                          <a
                            href={it.shipping_track_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
                          >
                            Rastrear <ExternalLink size={12} />
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      {showActions && (
                        <td className="px-3 py-2">
                          <div className="flex flex-col items-center gap-1">
                            {canMarkDelivered && (
                              <Button
                                variant="success"
                                className="!px-2 !py-1 text-xs"
                                icon={<CheckCircle size={14} />}
                                onClick={() => setDeliverTarget(it)}
                              >
                                Entregue
                              </Button>
                            )}
                            {correiosObjectsOf(it).length > 0 && (
                              <Button
                                variant="outline"
                                className="!px-2 !py-1 text-xs whitespace-nowrap"
                                icon={<LifeBuoy size={14} />}
                                onClick={() => setTicketTarget(it)}
                              >
                                Abrir ticket
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {ticketTarget && (
        <CorreiosTicketModal
          isOpen={!!ticketTarget}
          objectCodes={correiosObjectsOf(ticketTarget)}
          orderLabel={ticketTarget.order_num_ecommerce || ticketTarget.order_id}
          recipientName={ticketTarget.customer_name}
          onClose={() => setTicketTarget(null)}
        />
      )}

      {deliverTarget && (
        <MarkDeliveredModal
          isOpen={!!deliverTarget}
          orderId={deliverTarget.order_id}
          orderLabel={deliverTarget.order_num_ecommerce || deliverTarget.order_id}
          onClose={() => setDeliverTarget(null)}
          onConfirmed={() => {
            setDeliverTarget(null);
            // O pedido deixa de estar "Enviado", então some da lista após recarregar.
            load();
          }}
        />
      )}
    </div>
  );
};

export default PendingDeliveryReportView;
